import {
  Fragment,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Download, Loader2, Lock, RefreshCw, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { canManageWorkspaceTeam } from "@/lib/auxiliaryRoleGuide";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import {
  TAX_SUMMARY_MONTH_KEYS,
  TAX_SUMMARY_MONTH_LABELS,
  buildTaxSummaryRegimenOptions,
  buildTaxSummaryNavigableCells,
  cloneWorkbook,
  countCfdiAutoCells,
  effectiveTaxSummaryRegimen,
  formatTaxSummaryAmount,
  getManualVsPrefillDiff,
  handleTaxSummaryGridKeyDown,
  isTaxSummaryCellEditable,
  isTaxSummaryCfdiDrilldownRow,
  listManualVsPrefillDiffs,
  workbookNeedsIvaPatternRepair,
  patchAuxiliarySheets,
  patchWorkbookCell,
  patchWorkbookMeta,
  parseTaxSummaryAmount,
  RESICO_IVA_SUBTOTAL_ROW_IDS,
  showTaxSummaryPfResicoPanel,
  syncWorkbookRegimenFromClient,
  taxSummaryCellTestId,
  taxSummaryMonthColumnClass,
  groupTaxSummaryDisplaySections,
  visibleLayoutRows,
  isTaxSummaryMonthClosed,
  taxSummaryMonthClosureLabel,
} from "@/lib/taxSummaryWorkbookConstants";
import { formatTaxSummaryWorkbookTitle } from "@/lib/taxSummaryWorkbookTitle";
import {
  applyTaxSummaryPastePatches,
  parseExcelClipboardText,
  planTaxSummaryExcelPaste,
} from "@/lib/taxSummaryExcelPaste";
import TaxSummaryAuxiliarySheetsEditor from "@/components/despacho/TaxSummaryAuxiliarySheetsEditor";
import TaxSummaryMonthClosurePanel from "@/components/despacho/TaxSummaryMonthClosurePanel";
import TaxSummaryAmountInput from "@/components/despacho/TaxSummaryAmountInput";
import TaxSummaryResicoPfPanel from "@/components/despacho/TaxSummaryResicoPfPanel";
import TaxSummaryCfdiDetailPanel from "@/components/despacho/TaxSummaryCfdiDetailPanel";
import TaxSummaryRetencionesInformePanel from "@/components/despacho/TaxSummaryRetencionesInformePanel";

function cellTitle(mode, fromCfdi, fromCalc, fromIvaDerived, manualDiff = null) {
  if (manualDiff) {
    const pre = formatTaxSummaryAmount(manualDiff.baseline);
    const man = formatTaxSummaryAmount(manualDiff.captured);
    const delta = manualDiff.diff == null
      ? "—"
      : formatTaxSummaryAmount(String(manualDiff.diff));
    return `Manual vs precálculo · Pre: ${pre || "—"} · Manual: ${man || "—"} · Δ: ${delta}`;
  }
  if (fromCalc) return "Calculado automáticamente";
  if (fromIvaDerived) return "Sugerido desde IVA ÷ 0.16 (editable; use Recalcular fórmulas para regenerar)";
  if (fromCfdi) return "Precargado automáticamente desde CFDI / Confronta (editable)";
  if (mode === "manual") return "Captura manual";
  if (mode === "coeficiente") return "Coeficiente del encabezado";
  return undefined;
}

function cellClassName({ mode, fromCfdi, fromCalc, fromIvaDerived, manualDiff = null }) {
  if (manualDiff?.hasDiff) return "bg-amber-50 text-amber-950";
  if (manualDiff) return "bg-amber-50/50 text-slate-800";
  if (fromCalc || mode === "calculated") return "bg-slate-100 text-slate-700";
  if (fromIvaDerived) return "bg-sky-50/80";
  if (fromCfdi || mode === "cfdi") return "bg-emerald-50/80";
  if (mode === "coeficiente") return "bg-amber-50/60";
  return "";
}

function layoutRowClass(kind, mode) {
  if (mode === "calculated") return "bg-slate-50 font-medium text-slate-800";
  if (kind === "section") return "bg-amber-50 font-semibold text-amber-950";
  if (kind === "total") return "bg-slate-100 font-semibold text-slate-900";
  if (kind === "note") return "bg-slate-50 text-[10px] text-slate-600 italic";
  return "bg-white";
}

const TaxSummaryWorkbookEditor = forwardRef(function TaxSummaryWorkbookEditor({
  fiscalClientId,
  clientName = "",
  year,
  readOnly = false,
  onSaved,
  onDirtyChange,
}, ref) {
  const title = useMemo(
    () => formatTaxSummaryWorkbookTitle(clientName),
    [clientName],
  );
  const { user } = useAuth();
  const canReopenClosedMonth = canManageWorkspaceTeam(user);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [exists, setExists] = useState(false);
  const [updatedAt, setUpdatedAt] = useState("");
  const [layout, setLayout] = useState([]);
  const [layoutFull, setLayoutFull] = useState([]);
  const [clientFiscal, setClientFiscal] = useState(null);
  const [auxiliaryLayout, setAuxiliaryLayout] = useState({});
  const [resicoBrackets, setResicoBrackets] = useState([]);
  const [workbook, setWorkbook] = useState(() => cloneWorkbook({ year }));
  const [dirty, setDirty] = useState(false);
  const [activeTab, setActiveTab] = useState("hoja1");
  const [recalculating, setRecalculating] = useState(false);
  const [drilldownRowId, setDrilldownRowId] = useState("");
  const recalcTimerRef = useRef(null);
  const recalcRequestIdRef = useRef(0);
  const workbookRef = useRef(workbook);
  const metaSaveTimerRef = useRef(null);
  const savedBaselineRef = useRef("");
  const loadRequestRef = useRef(0);

  useEffect(() => {
    // Cierre mensual y Observaciones ya no es pestaña superior; quedó bajo «Otros» en Hoja 1.
    if (activeTab === "cierre") setActiveTab("hoja1");
  }, [activeTab]);

  useEffect(() => {
    workbookRef.current = workbook;
  }, [workbook]);

  const workbookSignature = useCallback((wb) => {
    try {
      return JSON.stringify(wb);
    } catch {
      return "";
    }
  }, []);

  const resetSavedBaseline = useCallback((wb) => {
    savedBaselineRef.current = workbookSignature(wb);
  }, [workbookSignature]);

  const hasUnsavedChanges = useCallback(() => {
    if (dirty) return true;
    if (!savedBaselineRef.current) return false;
    return workbookSignature(workbook) !== savedBaselineRef.current;
  }, [dirty, workbook, workbookSignature]);

  const cfdiAutoCount = useMemo(() => countCfdiAutoCells(workbook), [workbook]);
  const regimen = effectiveTaxSummaryRegimen(workbook, clientFiscal);
  const regimenOptions = useMemo(
    () => buildTaxSummaryRegimenOptions(clientFiscal),
    [clientFiscal],
  );
  const regimenLabel = workbook?.meta?.regimen_label || clientFiscal?.regimen_label || "";
  const regimenFromClient = workbook?.meta?.regimen_inferred === "true"
    || workbook?.meta?.regimen_inferred === true;
  const isGeneralLey = regimen === "general_ley" || regimen === "general";
  const isResico = regimen === "resico";
  const showPfResicoPanel = showTaxSummaryPfResicoPanel(clientFiscal, regimen);
  const layoutSource = layoutFull.length ? layoutFull : layout;
  const displayLayout = useMemo(
    () => visibleLayoutRows(layoutSource, regimen, workbook),
    [layoutSource, regimen, workbook],
  );
  const manualVsPrefillDiffs = useMemo(
    () => listManualVsPrefillDiffs(workbook, displayLayout),
    [workbook, displayLayout],
  );
  const navigableCells = useMemo(
    () => buildTaxSummaryNavigableCells(displayLayout, readOnly, workbook),
    [displayLayout, readOnly, workbook],
  );
  const layoutSections = useMemo(
    () => groupTaxSummaryDisplaySections(displayLayout),
    [displayLayout],
  );
  const defaultCalcAccordionSections = useMemo(() => [], []);

  const loadWorkbook = useCallback(async () => {
    if (!fiscalClientId || !year) return;
    const requestToken = loadRequestRef.current + 1;
    loadRequestRef.current = requestToken;
    setLoading(true);
    try {
      let data = await despachoFiscalInfoApi.getTaxSummaryWorkbook({
        fiscalClientId,
        year,
      });
      if (loadRequestRef.current !== requestToken) return;

      let item = data?.item || {};
      let loaded = syncWorkbookRegimenFromClient(
        cloneWorkbook(item.workbook || { year }),
        item.client_fiscal || null,
      );

      // Precarga inicial o reparación IVA (mayo vacío / meses ~1k / algo distinto de v10).
      const cfdiCells = countCfdiAutoCells(loaded);
      const needsIvaRepair = workbookNeedsIvaPatternRepair(loaded);
      if (!readOnly && (cfdiCells === 0 || needsIvaRepair)) {
        try {
          const prefillData = await despachoFiscalInfoApi.prefillTaxSummaryFromCfdi({
            fiscalClientId,
            year,
            // La carga automática nunca reabre meses confirmados; la recarga
            // explícita del usuario sí puede actualizar precargas guardadas.
            overwrite: false,
          });
          if (loadRequestRef.current !== requestToken) return;
          item = prefillData?.item || item;
          loaded = syncWorkbookRegimenFromClient(
            cloneWorkbook(item.workbook || loaded),
            item.client_fiscal || null,
          );
          if (needsIvaRepair && cfdiCells > 0) {
            toast.message("IVA recalculado: ingresos PUE + complementos por FechaPago.");
          }
        } catch (prefillErr) {
          if (loadRequestRef.current !== requestToken) return;
          const parsed = parseFiscalInfoError(prefillErr);
          if (parsed.kind !== "not_found") {
            toast.error(parsed.message);
          }
        }
      }

      if (loadRequestRef.current !== requestToken) return;
      setExists(Boolean(item.exists));
      setUpdatedAt(item.updated_at || "");
      setLayout(Array.isArray(item.layout) ? item.layout : []);
      setLayoutFull(
        Array.isArray(item.layout_full) ? item.layout_full : (Array.isArray(item.layout) ? item.layout : []),
      );
      setClientFiscal(item.client_fiscal || null);
      setAuxiliaryLayout(item.auxiliary_layout || {});
      setResicoBrackets(Array.isArray(item.resico_brackets) ? item.resico_brackets : []);
      setWorkbook(loaded);
      resetSavedBaseline(loaded);
      setDirty(false);
    } catch (err) {
      if (loadRequestRef.current !== requestToken) return;
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      if (loadRequestRef.current === requestToken) {
        setLoading(false);
      }
    }
  }, [fiscalClientId, readOnly, resetSavedBaseline, year]);

  useEffect(() => {
    loadWorkbook();
  }, [loadWorkbook]);

  useEffect(() => {
    setActiveTab("hoja1");
  }, [fiscalClientId, year]);

  useEffect(() => () => {
    if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current);
    if (metaSaveTimerRef.current) clearTimeout(metaSaveTimerRef.current);
  }, []);

  const applyRecalculatedWorkbook = useCallback((item, baseWorkbook) => {
    const synced = syncWorkbookRegimenFromClient(
      cloneWorkbook(item?.workbook || baseWorkbook),
      item?.client_fiscal || clientFiscal,
    );
    setWorkbook(synced);
    setDirty(true);
  }, [clientFiscal]);

  const runRecalculateFormulas = useCallback(async (
    workbookSnapshot,
    { forceResicoIvaBases = false } = {},
  ) => {
    if (readOnly || !fiscalClientId || !year || !workbookSnapshot) return;
    const requestId = recalcRequestIdRef.current + 1;
    recalcRequestIdRef.current = requestId;
    setRecalculating(true);
    try {
      const data = await despachoFiscalInfoApi.recalculateTaxSummaryWorkbook({
        fiscalClientId,
        year,
        workbook: workbookSnapshot,
        forceResicoIvaBases,
      });
      if (recalcRequestIdRef.current !== requestId) return;
      applyRecalculatedWorkbook(data?.item || {}, workbookSnapshot);
      if (forceResicoIvaBases) {
        toast.success("Fórmulas recalculadas (incl. subtotales RESICO desde IVA).");
      }
    } catch (err) {
      if (recalcRequestIdRef.current === requestId) {
        toast.error(parseFiscalInfoError(err).message);
      }
    } finally {
      if (recalcRequestIdRef.current === requestId) {
        setRecalculating(false);
      }
    }
  }, [applyRecalculatedWorkbook, fiscalClientId, readOnly, year]);

  const scheduleRecalculateFormulas = useCallback((workbookSnapshot, { delayMs = 500 } = {}) => {
    if (readOnly || !fiscalClientId) return;
    if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current);
    recalcTimerRef.current = setTimeout(() => {
      runRecalculateFormulas(workbookSnapshot, { forceResicoIvaBases: false });
    }, delayMs);
  }, [fiscalClientId, readOnly, runRecalculateFormulas]);

  const handleRecalculateFormulas = () => {
    if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current);
    runRecalculateFormulas(workbook, { forceResicoIvaBases: true });
  };

  const handleSave = useCallback(async (snapshot = workbookRef.current, { quiet = false } = {}) => {
    if (readOnly || !fiscalClientId || !snapshot) return false;
    setSaving(true);
    try {
      const data = await despachoFiscalInfoApi.saveTaxSummaryWorkbook({
        fiscalClientId,
        year,
        workbook: snapshot,
        commitMonths: !quiet,
      });
      const item = data?.item || {};
      setExists(Boolean(item.exists));
      setUpdatedAt(item.updated_at || "");
      const saved = cloneWorkbook(item.workbook || snapshot);
      setWorkbook(saved);
      workbookRef.current = saved;
      resetSavedBaseline(saved);
      setDirty(false);
      if (!quiet) {
        toast.success(`Resumen Impuestos ${year} guardado.`);
      }
      onSaved?.(item);
      return true;
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [fiscalClientId, onSaved, readOnly, resetSavedBaseline, year]);

  const schedulePersistMeta = useCallback((snapshot) => {
    if (readOnly || !fiscalClientId) return;
    if (metaSaveTimerRef.current) clearTimeout(metaSaveTimerRef.current);
    metaSaveTimerRef.current = setTimeout(() => {
      void handleSave(snapshot, { quiet: true });
    }, 400);
  }, [fiscalClientId, handleSave, readOnly]);

  const commitMetaField = useCallback((field, rawValue, { amountMode } = {}) => {
    if (readOnly) return;
    const parsed = amountMode
      ? parseTaxSummaryAmount(rawValue, { amountMode })
      : String(rawValue ?? "").trim();
    setWorkbook((prev) => {
      const patch = { [field]: parsed };
      if (field === "regimen") {
        patch.regimen_inferred = "";
      }
      const next = patchWorkbookMeta(prev, patch);
      if (field === "coeficiente_utilidad" || field === "tasa_isr_general" || field === "regimen") {
        scheduleRecalculateFormulas(next);
      }
      workbookRef.current = next;
      schedulePersistMeta(next);
      return next;
    });
    setDirty(true);
  }, [readOnly, schedulePersistMeta, scheduleRecalculateFormulas]);

  useImperativeHandle(ref, () => ({
    isDirty: () => hasUnsavedChanges(),
    hasUnsavedChanges,
    save: handleSave,
    discardChanges: loadWorkbook,
  }), [handleSave, hasUnsavedChanges, loadWorkbook]);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const handleDownload = async () => {
    if (!fiscalClientId) return;
    setDownloading(true);
    try {
      if (dirty && !readOnly) {
        await despachoFiscalInfoApi.saveTaxSummaryWorkbook({
          fiscalClientId,
          year,
          workbook,
          commitMonths: false,
        });
        setDirty(false);
      }
      const blob = await despachoFiscalInfoApi.downloadTaxSummaryWorkbook({
        fiscalClientId,
        year,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Resumen_Impuestos_${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel descargado.");
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setDownloading(false);
    }
  };

  const handlePrefill = async (overwrite = true) => {
    if (readOnly || !fiscalClientId) return;
    setPrefilling(true);
    try {
      const data = await despachoFiscalInfoApi.prefillTaxSummaryFromCfdi({
        fiscalClientId,
        year,
        overwrite,
      });
      const item = data?.item || {};
      const next = cloneWorkbook(item.workbook || workbook);
      setWorkbook(next);
      setExists(Boolean(item.exists));
      setUpdatedAt(item.updated_at || "");
      setDirty(workbookSignature(next) !== savedBaselineRef.current);
      const mapped = data?.prefill?.stats?.mapped_rows ?? 0;
      const ivaEne = Number(data?.prefill?.values?.["Iva Causado del mes"]?.ene || 0);
      let msg = `Datos actualizados desde CFDI / Confronta (${mapped} conceptos). Se conservan capturas manuales.`;
      if (ivaEne > 0.005) {
        const fmt = (n) => n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        msg += ` IVA causado enero (PUE + complementos de pago) ${fmt(ivaEne)}.`;
      }
      toast.success(msg);
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setPrefilling(false);
    }
  };

  const patchCell = (rowId, monthKey, value) => {
    if (readOnly) return;
    setWorkbook((prev) => {
      const next = patchWorkbookCell(prev, rowId, monthKey, value);
      scheduleRecalculateFormulas(next);
      return next;
    });
    setDirty(true);
  };

  const handleExcelPaste = useCallback((event, { rowIdx, monthKey, editable }) => {
    if (readOnly || !editable) return;
    const text = event.clipboardData?.getData("text/plain");
    if (text == null || text === "") return;
    // Pegado simple de un solo valor: deja el comportamiento nativo del input.
    if (!String(text).includes("\t") && !String(text).includes("\n") && !String(text).includes("\r")) {
      return;
    }
    event.preventDefault();
    const matrix = parseExcelClipboardText(text);
    const plan = planTaxSummaryExcelPaste({
      matrix,
      startRowIdx: rowIdx,
      startMonthKey: monthKey,
      displayLayout,
      readOnly,
      workbook: workbookRef.current,
    });
    if (!plan.ok) {
      toast.warning(plan.error || "Los datos pegados no encajan (parámetros distintos).");
      return;
    }
    setWorkbook((prev) => {
      const next = applyTaxSummaryPastePatches(prev, plan.patches);
      scheduleRecalculateFormulas(next);
      return next;
    });
    setDirty(true);
    toast.success(plan.summary || "Datos pegados desde Excel.");
  }, [displayLayout, readOnly, scheduleRecalculateFormulas]);

  const handleClosureWorkbookItem = useCallback((item) => {
    if (!item?.workbook) return;
    const loaded = syncWorkbookRegimenFromClient(
      cloneWorkbook(item.workbook),
      clientFiscal,
    );
    setWorkbook(loaded);
    resetSavedBaseline(loaded);
    setDirty(false);
  }, [clientFiscal, resetSavedBaseline]);

  const renderMonthStatusRow = () => (
    <tr className="border-t-2 border-slate-200 bg-slate-50 font-medium text-slate-800">
      <td className="border-r border-slate-100 p-2 sticky left-0 z-10 bg-slate-50 w-8" aria-hidden />
      <td className="border-r border-slate-100 p-2 sticky left-8 z-10 bg-slate-50">
        Estatus del mes
      </td>
      {TAX_SUMMARY_MONTH_KEYS.map((monthKey) => {
        const closed = isTaxSummaryMonthClosed(workbook?.meta, monthKey);
        return (
          <td
            key={`status-${monthKey}`}
            className={`border-r border-slate-100 p-1 text-center text-[10px] ${taxSummaryMonthColumnClass(monthKey)}`}
          >
            <span className={closed ? "text-slate-600" : "text-emerald-700"}>
              {closed ? (
                <Lock className="inline h-3 w-3 mr-0.5" aria-hidden />
              ) : null}
              {taxSummaryMonthClosureLabel(workbook?.meta, monthKey)}
            </span>
          </td>
        );
      })}
    </tr>
  );

  const renderLayoutTableRow = (row, rowIdx) => {
    const kind = row.kind || "row";
    const label = row.label || "";
    const rowId = row.row_id || label;
    const mode = row.mode || "manual";
    const colSpan = 2 + TAX_SUMMARY_MONTH_KEYS.length;
    if (kind === "blank") {
      return <tr key={`blank-${rowIdx}`} className="h-2" aria-hidden />;
    }
    if (kind === "note") {
      return (
        <tr key={`note-${rowIdx}`} className={layoutRowClass(kind, mode)}>
          <td colSpan={colSpan} className="p-2 border-b border-slate-100">
            {label}
          </td>
        </tr>
      );
    }
    if (kind === "section") {
      return (
        <tr key={`${rowId}-${rowIdx}`} className={`border-b border-slate-100 ${layoutRowClass(kind, mode)}`}>
          <td className={`border-r border-slate-100 p-2 sticky left-0 z-10 w-8 ${layoutRowClass(kind, mode)}`} />
          <td
            colSpan={1 + TAX_SUMMARY_MONTH_KEYS.length}
            className={`border-r border-slate-100 p-2 sticky left-8 z-10 ${layoutRowClass(kind, mode)}`}
          >
            {label}
          </td>
        </tr>
      );
    }
    const showValues = kind === "row" || kind === "total";
    const canDrilldown = showValues && isTaxSummaryCfdiDrilldownRow(rowId);
    const drilldownOpen = canDrilldown && drilldownRowId === rowId;
    return (
      <Fragment key={`${rowId}-${rowIdx}`}>
      <tr className={`border-b border-slate-100 ${layoutRowClass(kind, mode)}`}>
        <td
          className={`border-r border-slate-100 p-1 align-middle sticky left-0 z-10 w-8 text-center ${layoutRowClass(kind, mode)}`}
        >
          {canDrilldown ? (
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-[#0F2D5C] cursor-pointer"
              checked={drilldownOpen}
              onChange={(e) => {
                if (e.target.checked) {
                  setDrilldownRowId(rowId);
                } else {
                  setDrilldownRowId("");
                }
              }}
              title="Ver CFDI de precarga (solo lectura)"
              aria-label={`Desglose CFDI de ${label}`}
              data-testid={`tax-summary-cfdi-drilldown-${rowId}`}
            />
          ) : null}
        </td>
        <td
          className={`border-r border-slate-100 p-2 align-middle sticky left-8 z-10 ${layoutRowClass(kind, mode)}`}
        >
          <span>{label}</span>
          {mode === "calculated" ? (
            <span className="block text-[9px] text-slate-500 font-normal">Cálculo automático</span>
          ) : null}
          {mode === "cfdi" ? (
            <span className="block text-[9px] text-emerald-700 font-normal">CFDI / manual</span>
          ) : null}
          {RESICO_IVA_SUBTOTAL_ROW_IDS.has(rowId) ? (
            <span className="block text-[9px] text-sky-700 font-normal">IVA ÷ 0.16 / editable</span>
          ) : null}
        </td>
        {showValues
          ? TAX_SUMMARY_MONTH_KEYS.map((monthKey) => {
              const sourceKey = `${rowId}.${monthKey}`;
              const source = workbook?.sources?.[sourceKey];
              const fromCfdi = source === "cfdi_auto" || source === "confronta_snapshot" || source === "visor_resico";
              const fromIvaDerived = source === "iva_derived" || source === "iva_locked";
              const fromCalc = source === "calculated" || mode === "calculated" || mode === "coeficiente";
              const value = workbook?.values?.[rowId]?.[monthKey] ?? "";
              const manualDiff = getManualVsPrefillDiff(workbook, rowId, monthKey);
              const editable = isTaxSummaryCellEditable(rowId, mode, monthKey, readOnly, workbook)
                && !fromCalc
                && mode !== "calculated"
                && mode !== "coeficiente";
              if (mode === "confirmacion") {
                return (
                  <td key={`${rowId}-${monthKey}`} className="border-r border-slate-100 p-0">
                    <select
                      className="h-7 w-full border-0 bg-transparent text-[11px] text-center"
                      value={value}
                      disabled={readOnly || !editable}
                      tabIndex={readOnly ? -1 : 0}
                      data-testid={taxSummaryCellTestId(rowIdx, monthKey)}
                      onChange={(e) => patchCell(rowId, monthKey, e.target.value)}
                      onKeyDown={(e) => handleTaxSummaryGridKeyDown(e, {
                        rowIdx,
                        monthKey,
                        navCells: navigableCells,
                      })}
                    >
                      <option value="">—</option>
                      <option value="si">✓</option>
                      <option value="no">✗</option>
                    </select>
                  </td>
                );
              }
              return (
                <td
                  key={`${rowId}-${monthKey}`}
                  className={`border-r border-slate-100 p-0 ${taxSummaryMonthColumnClass(monthKey)}`}
                >
                  <TaxSummaryAmountInput
                    value={value}
                    editable={editable}
                    amountMode={mode === "coeficiente" ? "coeficiente" : "money"}
                    tabIndex={editable ? 0 : -1}
                    onChange={(next) => patchCell(rowId, monthKey, next)}
                    onPaste={(e) => handleExcelPaste(e, { rowIdx, monthKey, editable })}
                    onKeyDown={(e) => {
                      if (!editable) return;
                      handleTaxSummaryGridKeyDown(e, {
                        rowIdx,
                        monthKey,
                        navCells: navigableCells,
                      });
                    }}
                    className={cellClassName({ mode, fromCfdi, fromCalc, fromIvaDerived, manualDiff })}
                    title={cellTitle(mode, fromCfdi, fromCalc, fromIvaDerived, manualDiff)}
                    data-testid={taxSummaryCellTestId(rowIdx, monthKey)}
                  />
                </td>
              );
            })
          : TAX_SUMMARY_MONTH_KEYS.map((monthKey) => (
              <td key={`${kind}-${monthKey}`} className="border-r border-slate-100" />
            ))}
      </tr>
      {drilldownOpen ? (
        <tr className="border-b border-slate-200 bg-slate-50/80">
          <td colSpan={colSpan} className="p-2">
            <TaxSummaryCfdiDetailPanel
              fiscalClientId={fiscalClientId}
              year={year}
              rowId={rowId}
              rowLabel={label}
              workbook={workbook}
              onClose={() => {
                setDrilldownRowId("");
              }}
            />
          </td>
        </tr>
      ) : null}
      </Fragment>
    );
  };

  const patchMeta = (field, value) => {
    if (readOnly) return;
    setWorkbook((prev) => {
      const patch = { [field]: value };
      if (field === "regimen") {
        patch.regimen_inferred = "";
      }
      const next = patchWorkbookMeta(prev, patch);
      if (field === "coeficiente_utilidad" || field === "tasa_isr_general" || field === "regimen") {
        scheduleRecalculateFormulas(next);
      }
      return next;
    });
    setDirty(true);
  };

  const patchAuxiliary = (auxiliarySheets) => {
    if (readOnly) return;
    setWorkbook((prev) => patchAuxiliarySheets(prev, auxiliarySheets));
    setDirty(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500 text-sm gap-2">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Cargando plantilla…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 min-h-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-[#0F2D5C] m-0">{title}</h1>
          <p className="text-xs text-slate-500 mt-1 mb-0">
            Año {year}. Captura manual, precarga desde CFDI / Confronta y cálculos como en el Excel.
            {cfdiAutoCount > 0 ? (
              <span className="text-emerald-700"> · {cfdiAutoCount} celdas precargadas</span>
            ) : null}
            {manualVsPrefillDiffs.length > 0 ? (
              <span className="text-amber-800">
                {" "}· {manualVsPrefillDiffs.length} ajuste{manualVsPrefillDiffs.length === 1 ? "" : "s"} manual
                vs precálculo
              </span>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!readOnly ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                disabled={prefilling}
                onClick={() => handlePrefill(true)}
                title="Llena celdas vacías y actualiza precargas CFDI/Confronta. No borra capturas manuales."
                data-testid="tax-summary-prefill-cfdi"
              >
                {prefilling ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" aria-hidden />
                ) : (
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                )}
                Actualizar desde CFDI
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-xs"
                disabled={recalculating}
                onClick={handleRecalculateFormulas}
                title="Recalcula ISR, IVA y totales con los datos capturados"
              >
                {recalculating ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" aria-hidden />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                )}
                Recalcular fórmulas
              </Button>
            </>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            disabled={downloading}
            onClick={handleDownload}
            data-testid="tax-summary-workbook-download"
          >
            {downloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" aria-hidden />
            ) : (
              <Download className="h-3.5 w-3.5 mr-1.5" aria-hidden />
            )}
            Descargar Excel
          </Button>
          {!readOnly ? (
            <Button
              type="button"
              size="sm"
              className="h-8 text-xs bg-[#0F2D5C] hover:bg-[#0a2248]"
              disabled={saving || !dirty}
              onClick={handleSave}
              data-testid="tax-summary-workbook-save"
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" aria-hidden />
              ) : (
                <Save className="h-3.5 w-3.5 mr-1.5" aria-hidden />
              )}
              Guardar
            </Button>
          ) : null}
        </div>
      </div>

      {manualVsPrefillDiffs.length > 0 ? (
        <div
          className="rounded-sm border border-amber-200 bg-amber-50/70 px-3 py-2 space-y-1.5"
          data-testid="tax-summary-manual-vs-prefill"
        >
          <p className="text-xs font-semibold text-amber-950 m-0">
            Relación precálculo vs captura manual
          </p>
          <p className="text-[10px] text-amber-900/80 m-0">
            El precálculo CFDI/Confronta se conserva aunque edite la celda. Use esta tabla para validar
            diferencias (aplica a todas las empresas).
          </p>
          <div className="max-h-40 overflow-auto rounded-sm border border-amber-100 bg-white">
            <table className="w-full text-[10px] border-collapse">
              <thead className="sticky top-0 bg-amber-50 text-amber-950">
                <tr>
                  <th className="text-left font-medium px-2 py-1 border-b border-amber-100">Concepto</th>
                  <th className="text-left font-medium px-2 py-1 border-b border-amber-100">Mes</th>
                  <th className="text-right font-medium px-2 py-1 border-b border-amber-100">Precálculo</th>
                  <th className="text-right font-medium px-2 py-1 border-b border-amber-100">Manual</th>
                  <th className="text-right font-medium px-2 py-1 border-b border-amber-100">Δ</th>
                </tr>
              </thead>
              <tbody>
                {manualVsPrefillDiffs.map((row) => (
                  <tr
                    key={`${row.rowId}.${row.monthKey}`}
                    className="border-b border-amber-50"
                    data-testid={`tax-summary-manual-diff-${row.rowId}-${row.monthKey}`}
                  >
                    <td className="px-2 py-1 text-slate-800">{row.label}</td>
                    <td className="px-2 py-1 text-slate-600">
                      {TAX_SUMMARY_MONTH_LABELS[row.monthKey] || row.monthKey}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-700">
                      {formatTaxSummaryAmount(row.baseline) || "—"}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-900 font-medium">
                      {formatTaxSummaryAmount(row.captured) || "—"}
                    </td>
                    <td className={`px-2 py-1 text-right tabular-nums font-medium ${
                      Number(row.diff) > 0 ? "text-emerald-800" : Number(row.diff) < 0 ? "text-rose-800" : "text-slate-600"
                    }`}
                    >
                      {row.diff == null ? "—" : formatTaxSummaryAmount(String(row.diff))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="min-h-0 flex flex-col flex-1">
        <TabsList className="w-fit">
          <TabsTrigger value="hoja1" className="text-xs">Hoja 1 — Resumen mensual</TabsTrigger>
          <TabsTrigger value="ret-a-favor" className="text-xs">Retenciones a favor (ingresos)</TabsTrigger>
          <TabsTrigger value="ret-x-pagar" className="text-xs">Retenciones x pagar (gastos)</TabsTrigger>
          <TabsTrigger value="aux" className="text-xs">Hojas 2–4 — Visor, diferencias y pérdidas</TabsTrigger>
        </TabsList>

        <TabsContent value="hoja1" className="mt-3 flex flex-col gap-4 min-h-0 data-[state=inactive]:hidden">
      <div className={`grid gap-3 max-w-5xl ${isGeneralLey ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-1 lg:grid-cols-2"}`}>
        <div className="space-y-1">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <Label className="text-xs">Régimen ISR (sección visible)</Label>
            {!readOnly ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-[11px]"
                disabled={recalculating}
                onClick={handleRecalculateFormulas}
                title={
                  isResico
                    ? "Recalcula ISR/IVA y regenera ventas/gastos grabados desde IVA ÷ 0.16"
                    : "Recalcula ISR, IVA y totales con los datos capturados"
                }
                data-testid="tax-summary-recalculate-near-regimen"
              >
                {recalculating ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" aria-hidden />
                ) : (
                  <RefreshCw className="h-3 w-3 mr-1" aria-hidden />
                )}
                Recalcular fórmulas
              </Button>
            ) : null}
          </div>
          <Select
            value={regimen || "general_ley"}
            disabled={readOnly}
            onValueChange={(v) => patchMeta("regimen", v)}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Seleccione régimen" />
            </SelectTrigger>
            <SelectContent>
              {regimenOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {regimenLabel ? (
            <p className="text-[10px] text-slate-500 m-0">
              Según cliente: {clientFiscal?.regimen_fiscal || regimenLabel}
              {regimenFromClient ? " (precargado del catálogo)" : ""}
            </p>
          ) : null}
          {isResico ? (
            <p className="text-[10px] text-sky-800 m-0">
              Ventas cobradas grabadas y gastos gravados se sugieren como IVA ÷ 0.16.
              Si los modifica o guarda, se conservan hasta pulsar «Recalcular fórmulas».
            </p>
          ) : null}
        </div>
        {isGeneralLey ? (
          <>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="tax-summary-coeficiente-utilidad">
                Coeficiente de utilidad
              </Label>
              <Input
                id="tax-summary-coeficiente-utilidad"
                value={workbook?.meta?.coeficiente_utilidad || ""}
                readOnly={readOnly}
                inputMode="decimal"
                className="h-8 text-xs tabular-nums bg-white border-slate-300"
                placeholder="0.0535"
                data-testid="tax-summary-coeficiente-utilidad"
                onChange={(e) => patchMeta("coeficiente_utilidad", e.target.value)}
                onBlur={(e) => commitMetaField("coeficiente_utilidad", e.target.value, { amountMode: "coeficiente" })}
              />
              <p className="text-[10px] text-slate-500 m-0">
                Se guarda al salir del campo y se conserva hasta que lo cambie (suele actualizarse en marzo).
                Puede dejarlo vacío y completarlo después en Hojas 2–4.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="tax-summary-tasa-isr-general">
                Tasa ISR general
              </Label>
              <Input
                id="tax-summary-tasa-isr-general"
                value={workbook?.meta?.tasa_isr_general || ""}
                readOnly={readOnly}
                inputMode="decimal"
                className="h-8 text-xs tabular-nums bg-white border-slate-300"
                placeholder="0.30"
                data-testid="tax-summary-tasa-isr-general"
                onChange={(e) => patchMeta("tasa_isr_general", e.target.value)}
                onBlur={(e) => commitMetaField("tasa_isr_general", e.target.value)}
              />
            </div>
          </>
        ) : null}
      </div>

      {showPfResicoPanel ? (
        <TaxSummaryResicoPfPanel
          meta={workbook?.meta || {}}
          resicoBrackets={resicoBrackets}
          readOnly={readOnly}
          onMetaChange={(nextMeta) => {
            setWorkbook(patchWorkbookMeta(workbook, nextMeta));
            setDirty(true);
          }}
        />
      ) : null}

      <p className="text-xs text-slate-600 m-0">
        Expanda cada bloque para ver y capturar ISR, IVA y retenciones del mes.
      </p>

      <Accordion
        key={`${layoutSections.map((section) => section.section).join("|")}|cierre`}
        type="multiple"
        defaultValue={defaultCalcAccordionSections}
        className="space-y-2 rounded-sm border border-slate-200 bg-slate-50/60 p-2"
        data-testid="tax-summary-calc-sections"
      >
        {layoutSections.map((section) => (
          <AccordionItem
            key={section.section}
            value={section.section}
            className="rounded-sm border border-slate-200 bg-white px-3 shadow-sm"
            data-testid={`tax-summary-section-${section.section}`}
          >
            <AccordionTrigger
              compact
              className="text-sm font-semibold text-[#0F2D5C] hover:no-underline py-2"
            >
              {section.title}
            </AccordionTrigger>
            <AccordionContent className="pb-3">
              <div className="overflow-auto rounded-sm border border-slate-200 bg-white max-h-[min(70vh,520px)]">
                <table className="w-max min-w-full border-collapse text-[10px]">
                  <thead className="sticky top-0 z-20 bg-emerald-50">
                    <tr>
                      <th
                        className="border-b border-r border-slate-200 p-1 text-center font-semibold text-slate-700 w-8 sticky left-0 z-30 bg-emerald-50"
                        title="Marque para ver CFDI de precarga"
                      >
                        <span className="sr-only">CFDI</span>
                      </th>
                      <th className="border-b border-r border-slate-200 p-2 text-left font-semibold text-slate-700 min-w-[200px] max-w-[240px] sticky left-8 z-30 bg-emerald-50">
                        Concepto
                      </th>
                      {TAX_SUMMARY_MONTH_KEYS.map((key) => {
                        const closed = isTaxSummaryMonthClosed(workbook?.meta, key);
                        return (
                        <th
                          key={key}
                          className={`border-b border-r border-slate-200 p-1 text-center font-medium text-slate-600 ${taxSummaryMonthColumnClass(key)}`}
                        >
                          <span className="inline-flex items-center justify-center gap-0.5">
                            {closed ? <Lock className="h-3 w-3 text-slate-500" aria-hidden /> : null}
                            {TAX_SUMMARY_MONTH_LABELS[key]}
                          </span>
                        </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {section.rows.map(({ row, rowIdx }) => renderLayoutTableRow(row, rowIdx))}
                    {renderMonthStatusRow()}
                  </tbody>
                </table>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
        <AccordionItem
          value="cierre"
          className="rounded-sm border border-slate-200 bg-white px-3 shadow-sm"
          data-testid="tax-summary-section-cierre"
        >
          <AccordionTrigger
            compact
            className="text-sm font-semibold text-[#0F2D5C] hover:no-underline py-2"
          >
            Cierre mensual y Observaciones
          </AccordionTrigger>
          <AccordionContent className="pb-3">
            <TaxSummaryMonthClosurePanel
              fiscalClientId={fiscalClientId}
              year={year}
              readOnly={readOnly}
              canReopen={canReopenClosedMonth}
              currentUser={user}
              workbookMeta={workbook?.meta}
              onWorkbookItem={handleClosureWorkbookItem}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <p className="text-[10px] text-slate-500 m-0">
        Celdas verdes = CFDI / última Confronta; grises = calculadas; ámbar = coeficiente. La casilla antes del concepto abre la relación CFDI de precarga (solo lectura; exportable a Excel). ISR general usa ingresos acumulados × CU (Art. 14).
        {" "}Puedes pegar desde Excel (Ctrl+V): una fila reparte en meses; una columna, hacia abajo. Si no encaja, verás un aviso.
        {recalculating ? " Recalculando fórmulas…" : dirty ? " Cambios sin guardar." : exists ? ` Última sincronización: ${updatedAt ? new Date(updatedAt).toLocaleString("es-MX") : "guardado"}.` : " Plantilla nueva para este año."}
      </p>
        </TabsContent>

        <TabsContent value="ret-a-favor" className="mt-3 data-[state=inactive]:hidden">
          <TaxSummaryRetencionesInformePanel
            fiscalClientId={fiscalClientId}
            year={year}
            side="a_favor"
          />
        </TabsContent>

        <TabsContent value="ret-x-pagar" className="mt-3 data-[state=inactive]:hidden">
          <TaxSummaryRetencionesInformePanel
            fiscalClientId={fiscalClientId}
            year={year}
            side="x_pagar"
          />
        </TabsContent>

        <TabsContent value="aux" className="mt-3 data-[state=inactive]:hidden">
          <TaxSummaryAuxiliarySheetsEditor
            auxiliarySheets={workbook?.auxiliary_sheets || {}}
            auxiliaryLayout={auxiliaryLayout}
            onAuxiliaryChange={patchAuxiliary}
            readOnly={readOnly}
            workbookYear={year}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
});

TaxSummaryWorkbookEditor.displayName = "TaxSummaryWorkbookEditor";

export default TaxSummaryWorkbookEditor;
